/**
 * LE RÉCIT DE COMBAT — composé ICI, à partir des événements structurés.
 *
 * Le serveur renvoie des faits (`{type:"attaque", jet:14, total:19, dd:16, touche:true…}`) et jamais
 * une phrase : une phrase française sortie de l'API est une phrase qu'on ne traduira pas (§13).
 * C'est donc le front qui écrit, et c'est le bon endroit — il sait à qui il parle.
 *
 * TROIS RÈGLES TENUES ICI :
 *
 * 1. **Le jet est montré**, toujours : le d20, le modificateur, le seuil. Dans un jeu où l'on meurt
 *    pour de bon, un jet caché est insupportable — et le moteur étant déterministe, il peut tout
 *    justifier (design §3, « Le combat »).
 * 2. **Ce qui tue se lit deux fois** : le texte le dit, et le `ton` le colore. Mais le ton ne porte
 *    jamais l'information seul (accessibilité §5) — « manqué » est écrit, pas seulement grisé.
 * 3. **Une phrase se traduit entière, jamais recollée.** Ce module conjuguait : il tenait un sujet
 *    d'un côté, un verbe de l'autre, et les mettait bout à bout. Ça marche en français et nulle
 *    part ailleurs — l'ordre des mots, la place du pronom et l'accord ne sont pas les mêmes d'une
 *    langue à l'autre. On choisit donc entre des phrases ÉCRITES EN ENTIER, une par point de vue,
 *    et le traducteur les reçoit entières lui aussi. C'est le même reproche qu'on fait à l'API,
 *    appliqué au front.
 *
 * Fonction pure, sans React et sans DOM : c'est ce qui la rend testable, et elle l'est.
 */
import type { EvenementCombat } from "../api/types.js";
import { t, tn } from "../lexique/langue.js";
import { BLESSURES, ETATS } from "../lexique/mots.js";

export const ID_DU_HEROS = "heros";

export type TonDuRecit = "neutre" | "coup" | "encaisse" | "faste" | "funeste" | "souffle";

export interface JetMontre {
  d20: number;
  total: number;
  dd: number;
  reussi: boolean;
  critique?: boolean;
  echecCritique?: boolean;
}

export interface LigneDeRecit {
  texte: string;
  ton: TonDuRecit;
  jet?: JetMontre;
  /** Un tour qui commence ouvre un bloc : le récit se lit par tours, comme un compte rendu. */
  ouvreLeTour?: number;
}

export type Noms = Map<string, string>;

function nom(id: string, noms: Noms): string {
  if (id === ID_DU_HEROS) return t("Vous");
  return noms.get(id) ?? t("L'adversaire");
}

/** Le nom d'une créature vient de l'aventure : il ne se traduit pas, et il se met en bas de casse
 * quand il tombe au milieu d'une phrase. */
function complement(id: string, noms: Noms): string {
  return id === ID_DU_HEROS ? t("vous") : (noms.get(id) ?? t("l'adversaire")).toLowerCase();
}

/** Deux phrases, une par point de vue : celle où c'est vous qui agissez, celle où c'est l'autre.
 * ELLES ARRIVENT DÉJÀ TRADUITES, et ce n'est pas un détail : une clef qui ne se lit pas dans un
 * appel à `t()` est une clef que le test de couverture ne voit pas, donc une phrase qui repassera
 * au français le jour où on la retouchera. Traduire les deux branches pour n'en garder qu'une coûte
 * une recherche dans une table ; ne pas les voir coûte une régression silencieuse. */
function selonQuiAgit(acteur: string, vous: string, lautre: string): string {
  return acteur === ID_DU_HEROS ? vous : lautre;
}

/**
 * TROIS PHRASES, ET PAS DEUX : « Vous touchez le loup », « Le loup vous touche », et le cas où deux
 * autres se battent devant vous. C'est ce troisième cas qui interdisait de recoller un verbe à un
 * sujet — le complément français passe devant le verbe quand c'est vous qu'on vise, et derrière
 * sinon. Écrites en entier, les trois phrases se traduisent sans que rien ne se déplace.
 */
function selonLeCouple(acteur: string, cible: string, vous: string, contreVous: string, entreEux: string): string {
  if (acteur === ID_DU_HEROS) return vous;
  if (cible === ID_DU_HEROS) return contreVous;
  return entreEux;
}

export function composeLeRecit(evenements: EvenementCombat[], noms: Noms): LigneDeRecit[] {
  const lignes: LigneDeRecit[] = [];
  let dernierTour: number | null = null;

  for (const e of evenements) {
    switch (e.type) {
      case "combat_ouvert": {
        const ordre = e.ordre
          .map((c) => `${c.id === ID_DU_HEROS ? t("vous") : c.nom} (${c.initiative})`)
          .join(t(", puis "));
        lignes.push({ texte: t("Les armes sortent. L'ordre est tiré : {ordre}.", { ordre }), ton: "souffle" });
        break;
      }

      case "tour":
        // UN SEUL INTERTITRE PAR TOUR. Le moteur émet un « tour » par acteur ; deux « Tour 2 » de
        // suite dans la colonne se lisent comme un bégaiement, pas comme un compte rendu.
        if (dernierTour !== e.numero) {
          dernierTour = e.numero;
          lignes.push({ texte: t("Tour {numero}", { numero: e.numero }), ton: "neutre", ouvreLeTour: e.numero });
        }
        break;

      case "tour_perdu":
        lignes.push({
          texte: selonQuiAgit(
            e.acteur,
            t("Vous perdez votre tour — {etat}.", { etat: ETATS[e.cause].nom.toLowerCase() }),
            t("{acteur} perd son tour — {etat}.", {
              acteur: nom(e.acteur, noms),
              etat: ETATS[e.cause].nom.toLowerCase()
            })
          ),
          ton: e.acteur === ID_DU_HEROS ? "encaisse" : "faste"
        });
        break;

      case "attaque": {
        const geste = e.puissante ? t("une attaque puissante") : t("une attaque");
        const qui = { acteur: nom(e.acteur, noms), cible: complement(e.cible, noms) };
        const jet: JetMontre = {
          d20: e.jet,
          total: e.total,
          dd: e.dd,
          reussi: e.touche,
          critique: e.critique,
          echecCritique: e.echecCritique
        };

        if (e.echecCritique) {
          lignes.push({
            texte: selonQuiAgit(
              e.acteur,
              t("Vous manquez tout — un 1 naturel, et vous perdez votre réaction jusqu'au tour suivant."),
              t("{acteur} manque tout — un 1 naturel, et il perd sa réaction jusqu'au tour suivant.", {
                acteur: qui.acteur
              })
            ),
            ton: e.acteur === ID_DU_HEROS ? "encaisse" : "faste",
            jet
          });
          break;
        }

        if (e.esquive && e.esquive.reussie) {
          lignes.push({
            texte:
              e.cible === ID_DU_HEROS
                ? t("Vous esquivez {geste} de {acteur} — d20 {d20}, total {total}.", {
                    geste,
                    acteur: complement(e.acteur, noms),
                    d20: e.esquive.jet,
                    total: e.esquive.total
                  })
                : t("{cible} esquive {geste} — d20 {d20}, total {total}.", {
                    cible: nom(e.cible, noms),
                    geste,
                    d20: e.esquive.jet,
                    total: e.esquive.total
                  }),
            ton: e.cible === ID_DU_HEROS ? "faste" : "neutre",
            jet
          });
          break;
        }

        if (!e.touche) {
          lignes.push({
            texte: selonLeCouple(
              e.acteur,
              e.cible,
              t("Vous manquez {cible}.", { cible: qui.cible }),
              t("{acteur} vous rate.", { acteur: qui.acteur }),
              t("{acteur} rate {cible}.", qui)
            ),
            ton: e.acteur === ID_DU_HEROS ? "neutre" : "faste",
            jet
          });
          break;
        }

        lignes.push({
          texte: e.critique
            ? selonLeCouple(
                e.acteur,
                e.cible,
                t("Coup critique. Vous ouvrez {cible} : {degats} dégâts.", { cible: qui.cible, degats: e.degats }),
                t("Coup critique. {acteur} vous ouvre : {degats} dégâts.", { acteur: qui.acteur, degats: e.degats }),
                t("Coup critique. {acteur} ouvre {cible} : {degats} dégâts.", { ...qui, degats: e.degats })
              )
            : selonLeCouple(
                e.acteur,
                e.cible,
                t("Vous touchez {cible} : {degats} dégâts.", { cible: qui.cible, degats: e.degats }),
                t("{acteur} vous touche : {degats} dégâts.", { acteur: qui.acteur, degats: e.degats }),
                t("{acteur} touche {cible} : {degats} dégâts.", { ...qui, degats: e.degats })
              ),
          ton: e.acteur === ID_DU_HEROS ? "coup" : "encaisse",
          jet
        });
        break;
      }

      case "parade":
        lignes.push({
          texte: selonQuiAgit(
            e.acteur,
            t("Vous parez : +{bonus} en Défense jusqu'au tour suivant.", { bonus: e.bonus }),
            t("{acteur} pare : +{bonus} en Défense jusqu'au tour suivant.", {
              acteur: nom(e.acteur, noms),
              bonus: e.bonus
            })
          ),
          ton: "souffle"
        });
        break;

      case "posture_esquive":
        lignes.push({
          texte: selonQuiAgit(
            e.acteur,
            t("Vous vous mettez en garde : +{bonus} à l'esquive jusqu'au tour suivant.", { bonus: e.bonus }),
            t("{acteur} se met en garde : +{bonus} à l'esquive jusqu'au tour suivant.", {
              acteur: nom(e.acteur, noms),
              bonus: e.bonus
            })
          ),
          ton: "souffle"
        });
        break;

      case "capacite":
        lignes.push({
          texte: t("{acteur} — {capacite} : {detail}.", {
            acteur: nom(e.acteur, noms),
            capacite: e.nom,
            detail: e.detail
          }),
          ton: "souffle"
        });
        break;

      case "sort":
        // Le sort s'annonce AVANT le jet qui suit : « vous lancez le Trait de givre », puis « 14
        // contre 12, neuf dégâts ». L'inverse se lirait à l'envers.
        lignes.push({
          texte: selonQuiAgit(
            e.acteur,
            t("Vous lancez {sort} sur {cible} — {cout} d'Essence.", {
              sort: e.nom,
              cible: nom(e.cible, noms),
              cout: e.cout
            }),
            t("{acteur} lance {sort} sur {cible} — {cout} d'Essence.", {
              acteur: nom(e.acteur, noms),
              sort: e.nom,
              cible: nom(e.cible, noms),
              cout: e.cout
            })
          ),
          ton: "souffle"
        });
        break;

      case "objet_utilise":
        lignes.push({
          texte: selonQuiAgit(
            e.acteur,
            t("Vous usez d'un objet : {detail}.", { detail: e.detail }),
            t("{acteur} use d'un objet : {detail}.", { acteur: nom(e.acteur, noms), detail: e.detail })
          ),
          ton: "souffle"
        });
        break;

      case "dissimulation":
        lignes.push({
          texte: e.reussie
            ? selonQuiAgit(
                e.acteur,
                t("Vous disparaissez de la vue."),
                t("{acteur} disparaît de la vue.", { acteur: nom(e.acteur, noms) })
              )
            : selonQuiAgit(
                e.acteur,
                t("Vous ne trouvez pas d'ombre où vous mettre."),
                t("{acteur} ne trouve pas d'ombre où se mettre.", { acteur: nom(e.acteur, noms) })
              ),
          ton: e.reussie ? "faste" : "neutre",
          jet: { d20: e.jet, total: e.jet, dd: e.dd, reussi: e.reussie }
        });
        break;

      case "fuite":
        lignes.push({
          texte: e.reussie
            ? selonQuiAgit(
                e.acteur,
                t("Vous rompez le combat et partez."),
                t("{acteur} rompt le combat et part.", { acteur: nom(e.acteur, noms) })
              )
            : selonQuiAgit(
                e.acteur,
                t("Vous tentez de fuir, sans y parvenir."),
                t("{acteur} tente de fuir, sans y parvenir.", { acteur: nom(e.acteur, noms) })
              ),
          ton: e.reussie ? "souffle" : "encaisse",
          jet: { d20: e.jet, total: e.jet, dd: e.dd, reussi: e.reussie }
        });
        break;

      case "etat_pose": {
        const duree =
          e.tours === null ? t("Tant que ce n'est pas soigné.") : tn(e.tours, "{n} tour.", "{n} tours.");
        lignes.push({
          texte: selonQuiAgit(
            e.cible,
            t("Vous êtes {etat} — {effet} {duree}", {
              etat: ETATS[e.etat].nom.toLowerCase(),
              effet: ETATS[e.etat].effet,
              duree
            }),
            t("{cible} est {etat} — {effet} {duree}", {
              cible: nom(e.cible, noms),
              etat: ETATS[e.etat].nom.toLowerCase(),
              effet: ETATS[e.etat].effet,
              duree
            })
          ),
          ton: e.cible === ID_DU_HEROS ? "funeste" : "faste"
        });
        break;
      }

      case "etat_tick":
        lignes.push({
          texte: tn(e.degats, "{etat} : {n} dégât sur {cible}.", "{etat} : {n} dégâts sur {cible}.", {
            etat: ETATS[e.etat].nom,
            cible: complement(e.cible, noms)
          }),
          ton: e.cible === ID_DU_HEROS ? "encaisse" : "neutre"
        });
        break;

      case "etat_dissipe":
        lignes.push({
          texte: t("{etat} se dissipe sur {cible}.", {
            etat: ETATS[e.etat].nom,
            cible: complement(e.cible, noms)
          }),
          ton: "neutre"
        });
        break;

      case "blessure":
        lignes.push({
          texte: selonQuiAgit(
            e.cible,
            t("Blessure grave — {blessure} : {effet} Elle repartira avec vous.", {
              blessure: BLESSURES[e.blessure].nom.toLowerCase(),
              effet: BLESSURES[e.blessure].effet
            }),
            t("Blessure grave — {blessure} : {effet} Elle repartira avec lui.", {
              blessure: BLESSURES[e.blessure].nom.toLowerCase(),
              effet: BLESSURES[e.blessure].effet
            })
          ),
          ton: e.cible === ID_DU_HEROS ? "funeste" : "faste"
        });
        break;

      case "chute":
        lignes.push({
          texte:
            e.cible === ID_DU_HEROS
              ? t("Vous tombez.")
              : t("{cible} s'effondre.", { cible: nom(e.cible, noms) }),
          ton: e.cible === ID_DU_HEROS ? "funeste" : "coup"
        });
        break;

      case "fin": {
        const butin = e.butin.or > 0 ? ` ${tn(e.butin.or, "{n} pièce ramassée.", "{n} pièces ramassées.")}` : "";
        const objets = e.butin.objets.length
          ? ` ${tn(e.butin.objets.length, "{n} objet sur le corps.", "{n} objets sur le corps.")}`
          : "";
        if (e.issue === "victoire") {
          lignes.push({ texte: t("Victoire, en {tours} tours.", { tours: e.tours }) + butin + objets, ton: "faste" });
        } else if (e.issue === "fuite") {
          lignes.push({ texte: t("Vous rompez le combat après {tours} tours.", { tours: e.tours }), ton: "souffle" });
        } else {
          lignes.push({ texte: t("Défaite, au tour {tours}.", { tours: e.tours }), ton: "funeste" });
        }
        break;
      }
    }
  }

  return lignes;
}
